import { type ReactNode, useMemo, useState } from "react"
import type { HarnessId, HarnessStreamEvent } from "../../providerEvents/harnessEventTypes"
import type { TaskSessionDebugInfo } from "../../store/TaskModel"
import { useCodeStore } from "../../store/context"
import { FileViewer } from "../FilesAndDiffs"
import { CopyTextButton, MarkdownMessage } from "../MarkdownMessage"
import {
    type CommentContext,
    type DisplayContext,
    type GroupWithMeta,
    type MergedGroup,
    type MessageGroup,
    type RenderableItem,
    completeMessageGroups,
    groupStreamEvents,
} from "../events/messageGroups"
import { presentedGroupId } from "../events/presentation"
import { TaskSessionCard } from "../sessions/TaskSessionCard"
import { taskSessionsFromLaunchGroup } from "../sessions/taskSessionLaunch"
import { ActivityDigest } from "./ActivityDigest"
import { ConversationRow } from "./ConversationRow"
import { getRenderMode } from "./getRenderMode"
import { groupByRenderMode } from "./groupByRenderMode"
import { getRenderer } from "./renderers"
import { InlineWrapper } from "./wrappers/InlineWrapper"
import { PillGroup } from "./wrappers/PillGroup"
import { RowWrapper } from "./wrappers/RowWrapper"

export interface SessionInfo {
    sessionId?: string
    parentSessionId?: string
}

export interface InlineMessagesProps {
    events: HarnessStreamEvent[]
    harnessId: HarnessId
    sessionInfo?: SessionInfo
    taskId: string
    taskSessions?: readonly TaskSessionDebugInfo[]
    actionEventId: string
    messageGroups?: MessageGroup[]
    latestPlanGroupId?: string | null
    turnCompleted?: boolean
}

function getGroupId(group: MergedGroup, index: number): string {
    return presentedGroupId(group, index)
}

export interface PreparedInlineMessageRow {
    id: string
    renderable: RenderableItem
    latestTodoGroupId: string | null
    launchedSession?: TaskSessionDebugInfo
}

export function prepareInlineMessageRows({
    messageGroups,
    messageGroupIds,
    latestPlanGroupId,
    taskSessions,
    turnCompleted = false,
}: {
    messageGroups: MessageGroup[]
    messageGroupIds?: readonly string[]
    latestPlanGroupId?: string | null
    taskSessions?: readonly TaskSessionDebugInfo[]
    turnCompleted?: boolean
}): PreparedInlineMessageRow[] {
    const groups = turnCompleted ? completeMessageGroups(messageGroups) : messageGroups
    const identified = groups.map((group, index) => ({ group, id: messageGroupIds?.[index] ?? getGroupId(group, index) }))
    const latestTodoGroupId = latestPlanGroupId === undefined ? (identified.findLast(({ group }) => group.type === "todoWrite")?.id ?? null) : latestPlanGroupId
    const launchSessionsById = new Map<string, TaskSessionDebugInfo>()
    const seenLaunchSessionIds = new Set<string>()
    const withMeta: GroupWithMeta[] = identified.flatMap(({ group, id }) => {
        const ctx: DisplayContext = {
            isLatestTodoGroup: group.type === "todoWrite" && id === latestTodoGroupId,
        }
        const launchedSessions = group.type === "tool" || group.type === "orchestration" ? taskSessionsFromLaunchGroup(group, taskSessions) : []
        const launchRows = launchedSessions.flatMap((launchedSession) => {
            const launchedSessionId = launchedSession.nestSessionId
            if (!launchedSessionId || seenLaunchSessionIds.has(launchedSessionId)) return []
            seenLaunchSessionIds.add(launchedSessionId)
            const launchId = `${id}:session:${launchedSessionId}`
            launchSessionsById.set(launchId, launchedSession)
            return [{ group, mode: "row" as const, id: launchId }]
        })
        if (group.type === "tool" && launchedSessions.length > 0) return launchRows
        return [{ group, mode: getRenderMode(group, ctx), id }, ...launchRows]
    })

    return groupByRenderMode(withMeta).map((renderable) => {
        const rowId = inlineMessageRowId(renderable)
        const launchedSession = renderable.mode === "row" ? launchSessionsById.get(renderable.item.id) : undefined
        return {
            id: rowId,
            renderable,
            latestTodoGroupId,
            ...(launchedSession ? { launchedSession } : {}),
        }
    })
}

function inlineMessageRowId(renderable: RenderableItem): string {
    if (renderable.mode === "inline" || renderable.mode === "row") return renderable.item.id
    // History is prepended, so anchor a merged row to its newest constituent.
    // Adding older adjacent pills or digest entries then preserves the existing
    // row identity and TanStack can retain its measured scroll anchor.
    const anchorId = renderable.items.at(-1)?.id ?? "empty"
    return `${renderable.mode}:${anchorId}`
}

export function InlineMessages({
    events,
    harnessId,
    sessionInfo: _sessionInfo,
    taskId,
    taskSessions,
    actionEventId,
    messageGroups,
    latestPlanGroupId,
    turnCompleted = false,
}: InlineMessagesProps) {
    const parsedGroups = useMemo(() => messageGroups ?? groupStreamEvents(events, harnessId), [events, harnessId, messageGroups])
    const rows = useMemo(
        () => prepareInlineMessageRows({ messageGroups: parsedGroups, latestPlanGroupId, taskSessions, turnCompleted }),
        [latestPlanGroupId, parsedGroups, taskSessions, turnCompleted]
    )
    const commentCtx: CommentContext = useMemo(() => ({ taskId, actionEventId }), [taskId, actionEventId])

    if (rows.length === 0) return null

    return (
        <ConversationRow tone="assistant">
            <div className="flex flex-col">
                {rows.map((row) => (
                    <InlineMessageRowContent key={row.id} row={row} taskId={taskId} taskSessions={taskSessions} commentContext={commentCtx} />
                ))}
            </div>
        </ConversationRow>
    )
}

export function InlineMessageRow({
    row,
    taskId,
    taskSessions,
    actionEventId,
    blockStart = true,
    blockEnd = true,
}: {
    row: PreparedInlineMessageRow
    taskId: string
    taskSessions?: readonly TaskSessionDebugInfo[]
    actionEventId: string
    blockStart?: boolean
    blockEnd?: boolean
}) {
    const commentContext: CommentContext = useMemo(() => ({ taskId, actionEventId }), [actionEventId, taskId])
    return (
        <ConversationRow tone="assistant" blockStart={blockStart} blockEnd={blockEnd}>
            <div className="flex flex-col">
                <InlineMessageRowContent row={row} taskId={taskId} taskSessions={taskSessions} commentContext={commentContext} />
            </div>
        </ConversationRow>
    )
}

function InlineMessageRowContent({
    row,
    taskId,
    taskSessions,
    commentContext,
}: {
    row: PreparedInlineMessageRow
    taskId: string
    taskSessions?: readonly TaskSessionDebugInfo[]
    commentContext: CommentContext
}) {
    const [expanded, setExpanded] = useState(false)
    const [expandedPillId, setExpandedPillId] = useState<string | null>(null)
    const item = row.renderable

    if (item.mode === "activityDigest") {
        return <ActivityDigest items={item.items} commentContext={commentContext} />
    }

    if (item.mode === "inline") {
        const renderer = getRenderer(item.item.group)
        return <InlineWrapper>{renderer.renderContent(item.item.group, commentContext)}</InlineWrapper>
    }

    if (item.mode === "row") {
        const { group, id } = item.item
        if (row.launchedSession) {
            return <TaskSessionCard taskId={taskId} session={row.launchedSession} taskSessions={taskSessions} />
        }
        const renderer = getRenderer(group)
        const isPending = "isPending" in group && group.isPending
        const isError = "isError" in group && group.isError
        const isPinnedOpen = (group.type === "todoWrite" && id === row.latestTodoGroupId) || (group.type === "agentActivity" && group.isPending)

        return (
            <RowWrapper
                icon={renderer.getIcon(group)}
                label={renderer.getLabel(group)}
                statusIcon={renderer.getStatusIcon?.(group)}
                headerInfo={renderer.getHeaderInfo?.(group)}
                isError={isError}
                isPending={isPending}
                collapsible={!isPinnedOpen}
                expanded={isPinnedOpen || expanded}
                onToggle={() => setExpanded((current) => !current)}
            >
                {renderer.renderContent(group, commentContext)}
            </RowWrapper>
        )
    }

    const pillItems = item.items.map(({ group, id }) => {
        const renderer = getRenderer(group)
        const isError = "isError" in group && group.isError
        const isComplete = group.type === "result" || group.type === "system" || ("result" in group && group.result !== undefined)

        return {
            id,
            icon: renderer.getIcon(group),
            label: renderer.getLabel(group),
            isError,
            isComplete,
            content: renderer.renderContent(group, commentContext),
        }
    })

    return <PillGroup items={pillItems} expandedId={expandedPillId} onToggle={(id) => setExpandedPillId((current) => (current === id ? null : id))} />
}

export function UserInputMessage({
    text,
    taskId,
    media,
    annotation,
    kind = "message",
}: {
    text: string
    taskId?: string
    media?: ReactNode
    annotation?: ReactNode
    kind?: "message" | "steer"
}) {
    const codeStore = useCodeStore()
    const renderMarkdown = codeStore.personalSettingsStore?.settings.current.renderMarkdownMessages ?? true
    const [expanded, setExpanded] = useState(false)
    const lines = text.split("\n")
    const isLong = lines.length > 7
    const displayText = expanded || !isLong ? text : lines.slice(0, 7).join("\n")

    return (
        <ConversationRow tone="user">
            <div className="overflow-hidden" data-user-message-kind={kind}>
                {annotation}
                {kind === "steer" && <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted">Steer</div>}
                {text &&
                    (renderMarkdown ? (
                        <div className="group/message-input relative min-w-0 pr-9">
                            <CopyTextButton
                                content={text}
                                label="Copy message input"
                                className="absolute top-0 right-0 z-20 opacity-0 transition-opacity group-hover/message-input:opacity-100 group-focus-within/message-input:opacity-100 [@media(hover:none)_and_(pointer:coarse)]:opacity-100"
                            />
                            <MarkdownMessage text={displayText} commentHandlers={null} taskId={taskId} variant="plain" density="compact" preserveLineBreaks />
                        </div>
                    ) : (
                        <FileViewer
                            file={{ name: "input.md", contents: displayText, lang: "markdown" }}
                            copyContent={text}
                            disableFileHeader
                            disableLineNumbers
                            commentHandlers={null}
                        />
                    ))}
                {isLong && (
                    <button
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                        className="btn w-full px-3 py-1 text-xs text-primary hover:bg-primary/10 transition-colors text-left"
                    >
                        {expanded ? "Show less" : `Show more (${lines.length - 7} more lines)`}
                    </button>
                )}
                {media}
            </div>
        </ConversationRow>
    )
}
